(() => {
  if (customElements.get('stance-offer-popup')) return;

  // sessionStorage can be unavailable in private or restricted browsing.
  const viewedThisPage = new Set();

  class StanceOfferPopup extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.frequency = this.dataset.frequency || 'page';
      this.storageKey = 'stanced:buy1-get1-75:v1:' +
        (this.frequency === 'session' ? 'visit' : window.location.pathname);

      this.querySelectorAll('[data-offer-close]').forEach(button => {
        button.addEventListener('click', () => this.dialog.close(), options);
      });

      // The native modal makes the page inert and restores focus when closed.
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('st-offer-is-open');
      }, options);
      this.dialog.addEventListener('keydown', event => {
        // Keep the underlying cart drawer's keyboard handler out of this modal.
        if (event.key === 'Tab' || event.key === 'Escape') event.stopPropagation();
        if (event.key !== 'Tab') return;
        const controls = [...this.dialog.querySelectorAll('a[href], button:not([disabled])')]
          .filter(control => control.getClientRects().length);
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }, options);

      const outside = event => {
        const box = this.dialog.getBoundingClientRect();
        return event.target === this.dialog &&
          (event.clientX < box.left || event.clientX > box.right ||
           event.clientY < box.top || event.clientY > box.bottom);
      };
      this.dialog.addEventListener('pointerdown', event => {
        this.backdropPressed = outside(event);
      }, options);
      this.dialog.addEventListener('click', event => {
        if (this.backdropPressed && outside(event)) this.dialog.close();
        this.backdropPressed = false;
      }, options);

      this.querySelector('[data-offer-shop]')?.addEventListener('click', event => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        if (this.dataset.currentProduct === 'true') {
          const form = document.querySelector('.spv2-form');
          if (form) {
            event.preventDefault();
            this.dialog.close();
            const firstChoice = form.querySelector(
              '[data-sp-option-input]:checked, select:not([disabled]), input[name="quantity"]:not([type="hidden"]), [data-sp-atc]'
            );
            firstChoice?.focus({ preventScroll: true });
            form.scrollIntoView({
              behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
              block: 'center'
            });
            return;
          }
        }
        this.dialog.close();
      }, options);

      window.addEventListener('pagehide', () => {
        window.clearTimeout(this.timer);
        if (this.dialog.open) this.dialog.close();
      }, options);
      window.addEventListener('pageshow', event => {
        if (event.persisted) this.schedule();
      }, options);

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:section:select', event => {
          if (event.detail?.sectionId === this.dataset.sectionId) this.open(true);
        }, options);
        document.addEventListener('shopify:section:deselect', event => {
          if (event.detail?.sectionId === this.dataset.sectionId) this.dialog.close();
        }, options);
      }

      this.schedule();
    }

    hasBeenViewed() {
      if (this.frequency === 'always' || window.Shopify?.designMode) return false;
      try {
        return sessionStorage.getItem(this.storageKey) === '1' || viewedThisPage.has(this.storageKey);
      } catch (_) {
        return viewedThisPage.has(this.storageKey);
      }
    }

    schedule() {
      window.clearTimeout(this.timer);
      if (this.hasBeenViewed() || this.dialog.open) return;
      const delay = Number(this.dataset.delay);
      this.timer = window.setTimeout(() => this.open(),
        (Number.isFinite(delay) && delay > 0 ? delay : 5) * 1000);
    }

    open(preview = false) {
      window.clearTimeout(this.timer);
      if (!this.isConnected || this.dialog.open || (!preview && this.hasBeenViewed())) return;

      const active = document.activeElement;
      const isTyping = active?.matches('input:not([type="radio"]):not([type="checkbox"]), textarea, select, [contenteditable="true"]');
      const anotherModal = document.querySelector(
        'dialog[open], [data-stance-drawer].is-open, .popup-modal--active, .st-mobile-panel.is-open, #shopify-pc__banner'
      );
      if (!preview && (document.hidden || isTyping || (anotherModal && anotherModal.getClientRects().length))) {
        this.timer = window.setTimeout(() => this.open(), 500);
        return;
      }

      this.dialog.showModal();
      document.documentElement.classList.add('st-offer-is-open');
      if (!window.Shopify?.designMode) {
        viewedThisPage.add(this.storageKey);
        try { sessionStorage.setItem(this.storageKey, '1'); } catch (_) { /* Use in-memory fallback. */ }
      }
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      if (this.dialog?.open) this.dialog.close();
      document.documentElement.classList.remove('st-offer-is-open');
      this.controller?.abort();
      this.controller = null;
    }
  }

  customElements.define('stance-offer-popup', StanceOfferPopup);
})();
